import moment from 'moment';
import lowerFirst from 'lodash/lowerFirst';
import { store } from '../../store';
import { gql, insertGraphQLFragments } from '../GraphQLClient';
import { fragmentUserFull, fragmentUserBasic, serializeUserBasic, serializeUserFull } from './serializeUser';
import { fragmentRoomBasic, serializeRoomBasic } from './serializeRoom';
import { fragmentFileFull, serializeFileFull } from './serializeFile';
import { fragmentIssueBasic } from './serializeIssue';
import { fragmentTask } from './serializeTask';
import { serializeIssueBasic } from './serializeIssue';
import { defaultsTo, stringifyTimePeriod } from '../../utils/utilsGlobal';
import { getUserFullName } from '../../utils/utilsChat';


export const fragmentMessageBasic = insertGraphQLFragments(gql`
  fragment fragmentMessageBasic on Message {
    id
    timestamp
    text
    ... on ManualMessage {
      sender { ...fragmentUserFull }
      attachments { ...fragmentFileFull }
      isDeleted
    }
    ... on MassMessage {
      sender { ...fragmentUserFull }
      attachments { ...fragmentFileFull }
    }
  }
`, { fragmentUserFull, fragmentFileFull });


export const fragmentMessageFull = insertGraphQLFragments(gql`
  fragment fragmentMessageFull on Message {
    
    ...fragmentMessageBasic

    ... on RoomMessage {
      isUnread
      isCaseOpen
      isSystem
      room { ...fragmentRoomBasic }
    }

    ... on ManualMessage {
      queued
      tempId
      editedAt
      mustOpenWidget
      responseToMessage {
        id
        text
        sender { ...fragmentUserBasic }
        attachments { name }
        isDeleted
      }
      mentions {
        value
        client { ...fragmentUserBasic }
      },
      issues { ...fragmentIssueBasic }
    }

    ... on ReminderSetMessage {
      __typename
      reminder { timestamp, type { title } }
      sender { ...fragmentUserFull }
    }

    ... on ReminderFiredMessage {
      __typename
      reminder { timestamp, type { title } }
      sender { ...fragmentUserFull }
    }

    ... on WebhookSetMessage {
      __typename
      task { ...fragmentTask }
      sender { ...fragmentUserFull }
    }

    ... on WebhookUpdatedMessage {
      __typename
      newStatus
      oldStatus
      task { ...fragmentTask }
    }

    ... on CaseNotClosedNotifyMessage {
      __typename
      timeIdle
    }

    ... on OperatorAssignedMessage {
      __typename
      assignedEmployee { ...fragmentUserBasic }
      previousAssignedEmployee { ...fragmentUserBasic }
    }

    ... on ScreenshotRequestedMessage {
      __typename
      sender { ...fragmentUserFull }
    }

    ... on CaseClosedMessage {
      __typename
      sender { ...fragmentUserFull }
    }
  }
`, {
  fragmentFileFull,
  fragmentUserFull,
  fragmentUserBasic,
  fragmentRoomBasic,
  fragmentIssueBasic,
  fragmentMessageBasic,
  fragmentTask,
});


export function serializeMessageBasic(data) {
  return {
    ...defaultsTo(data, {
      id: null,
      cursor: null,
      text: '',
      timestamp: '',
      isDeleted: false,
    }),
    sender: serializeUserFull(data?.sender),
    attachments: (data?.attachments || []).map(serializeFileFull),
  };
}


export function serializeMessageFull(data){
  const { currentOperator } = store.getState();
  const { room, issues, isSystem, mentions, responseToMessage } = data || {};

  const serializedMessageBasic = serializeMessageBasic(data);
  const isLinkableToIssue = serializedMessageBasic.sender.isClient;
  const isEditable = !isSystem && (
    serializedMessageBasic.sender.isCurrentOperator || (serializedMessageBasic.sender.isOperator && currentOperator.isManager)
  );

  return {
    ...serializedMessageBasic,
    isLinkableToIssue,
    isEditable,
    ...defaultsTo(data, {
      isUnread: false,
      isCaseOpen: false,
      isSystem: false,
      isSubmitting: false,
      submissionError: null,
      editedAt: '',
      tempId: null,
      queued: null,
      mustOpenWidget: false,
    }),
    responseToMessage: serializeMessageBasic(responseToMessage),
    mentions: (mentions || []).map(mention => ({
      client: serializeUserBasic(mention.client),
      value: mention.value,
    })),
    room: serializeRoomBasic(room),
    linkedIssues: (issues || []).map(serializeIssueBasic),
    systemData: serializeSystemData(data),
  };
}


export function serializeSystemData(data){
  if (!data?.isSystem) {
    return {};
  }
  const {
    sender,
    __typename,
    timeIdle,
    task,
    newStatus,
    oldStatus,
    reminder,
    assignedEmployee,
    previousAssignedEmployee,
  } = data || {};

  let textFormatted = [];
  let textPlain = '';
  let isHighlightedAttention = false;
  let isHighlightedSuccess = false;

  const type = __typename;
  const senderName = getUserFullName(sender);
  const taskIsJira = task?.provider === 'JIRA';
  const taskTitle = task?.providerId === task?.title
    ? task?.providerId
    : task?.providerId + ' ' + task?.title;

  let reminderTitle = reminder?.type?.title || '';
  let reminderDateString = '';
  if (reminder?.timestamp) {
    reminderDateString = moment(reminder?.timestamp).calendar(null, {
      sameDay: '[Сегодня к] LT',
      nextDay: '[Завтра к] LT',
      lastDay: 'D MMMM [в] LT',
      nextWeek: '[в] dddd [в] LT',
      lastWeek: '[в] dddd [в] LT',
      sameElse: 'D MMMM [в] LT'
    });
  }

  if (type === 'ReminderSetMessage') {
    textFormatted = [
      { text: reminderTitle, isPrimary: true },
      { text: `${reminderDateString}, ${senderName}` },
    ];
    textPlain = `${reminderTitle} ${lowerFirst(reminderDateString)}`;
  }
  else if (type === 'ReminderFiredMessage') {
    textFormatted = [
      { text: `Напоминание: ${reminderTitle}`, isPrimary: true },
      { text: `${reminderDateString}, ${senderName}` },
    ];
    textPlain = `Напоминание: ${reminderTitle} ${lowerFirst(reminderDateString)}`;
    isHighlightedAttention = true;
  }
  else if (type === 'ScreenshotRequestedMessage') {
    textFormatted = [
      { text: 'Запрошен скриншот экрана', isPrimary: true },
      { text: senderName }
    ];
    textPlain = `Запрошен скриншот экрана`;
  }
  else if (type === 'WebhookSetMessage' && taskIsJira) {
    textFormatted = [
      { text: 'Jira', isPrimary: true },
      { text: taskTitle, url: task.url },
    ];
    textPlain = `Jira, ${taskTitle}`;
  }
  else if (type === 'WebhookUpdatedMessage' && taskIsJira) {
    const jiraStatusChangeText = oldStatus && newStatus
      ? `${oldStatus} → ${newStatus}`
      : 'обновление';
    textFormatted = [
      { text: jiraStatusChangeText, isPrimary: true },
      { text: taskTitle, url: task.url },
    ];
    textPlain = `Jira, ${jiraStatusChangeText}, ${taskTitle}`;
  }
  else if (type === 'CaseClosedMessage') {
    textFormatted = [
      { text: 'Кейс завершен', isPrimary: true },
      { text: senderName },
    ];
    textPlain = 'Кейс завершен';
    isHighlightedSuccess = true;
  }
  else if (type === 'CaseNotClosedNotifyMessage') {
    const millisecondsIdle = moment.duration(timeIdle).asMilliseconds();
    const stringTimeIdle = stringifyTimePeriod(millisecondsIdle, {
      format: 'string',
      customDict: {
        minutes: ['минуту', 'минуты', 'минут'],
      },
    });
    textFormatted = [
      { text: `${stringTimeIdle} ничего не происходит`, isPrimary: true },
      { text: 'Напишите клиенту или завершите кейс' },
    ];
    textPlain = `${stringTimeIdle} ничего не происходит. Напишите клиенту или завершите кейс`;
    isHighlightedAttention = true;
  }
  else if (type === 'OperatorAssignedMessage') {
    const currentAssignedOperator = getUserFullName(assignedEmployee);
    textFormatted = [
      { text: 'Назначен ответственный', isPrimary: true },
      { text: currentAssignedOperator },
    ];
    textPlain = `Назначен ответственный ${currentAssignedOperator}`;
    if (previousAssignedEmployee?.id) {
      textFormatted = [
        { text: 'Новый ответственный', isPrimary: true },
        { text: currentAssignedOperator + ' ← ' + getUserFullName(previousAssignedEmployee) },
      ];
      textPlain = `Новый ответственный ${currentAssignedOperator} ← ${getUserFullName(previousAssignedEmployee)}`;
    }
    isHighlightedAttention = true;
  }
  return {
    type,
    textPlain,
    textFormatted,
    isHighlightedSuccess,
    isHighlightedAttention,
  };
}
